import { useMemo, useState } from 'react';
import { LAB_THRESHOLDS } from '@shared/rules';
import type { LabEntry } from '@shared/domain';
import { useAuth } from '@/state/auth';
import { useCalibrate, useESignCreate, useLabChain, useLabCorrect, useLabEntries, useLabQc, useLabSubmit } from '@/lib/query';
import { ts } from '@/lib/format';
import { Badge, Card, DataTable, EmptyHint, SectionTitle, StatusPill } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

function shanghaiLocalNow(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));

export default function LabLoop() {
  const { user } = useAuth();
  const { data: lab } = useLabEntries();
  const submit = useLabSubmit();
  const calibrate = useCalibrate();
  const correct = useLabCorrect();
  const createSig = useESignCreate();

  const [sampleAt, setSampleAt] = useState(shanghaiLocalNow());
  const [ph, setPh] = useState('');
  const [nh3n, setNh3n] = useState('');
  const [no2, setNo2] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: chain } = useLabChain(selectedId);

  const [calMetric, setCalMetric] = useState<'ph' | 'nh3n' | 'no2'>('nh3n');
  const [calChannel, setCalChannel] = useState('demo-ch-ph');
  const [calOffset, setCalOffset] = useState('0.05');

  const effective = lab?.effective ?? [];
  const selected = useMemo(() => effective.find((e) => e.id === selectedId) ?? null, [effective, selectedId]);

  const doSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      const res = await submit.mutateAsync({
        sampleAt: new Date(sampleAt).toISOString(),
        ph: numOrNull(ph),
        nh3n: numOrNull(nh3n),
        no2: numOrNull(no2),
      });
      setMsg(res.bypass ? '已提交（PLC 快照旁路，禁止发起校准）' : '已提交并绑定 PLC 快照');
      setPh(''); setNh3n(''); setNo2('');
    } catch (err) {
      setMsg((err as Error).message);
    }
  };

  const doCalibrate = async () => {
    if (!selected) { setMsg('请先在右侧选择一条生效化验记录'); return; }
    setMsg(null);
    try {
      const pw = window.prompt('二次认证：输入账户口令以发起校准提议');
      if (!pw) return;
      const sig = await createSig.mutateAsync({ action: 'lab.calibrate', resourceType: 'lab', resourceId: selected.id, meaning: `校准 ${calMetric} 通道 ${calChannel}`, payload: { metric: calMetric, channel: calChannel }, password: pw });
      await calibrate.mutateAsync({ entryId: selected.id, metric: calMetric, channel: calChannel, absoluteOffset: Number(calOffset), signatureId: sig.id });
      setMsg('校准提议已提交（已电子签名）');
    } catch (err) {
      setMsg((err as Error).message);
    }
  };

  const doCorrect = async (e: LabEntry) => {
    setMsg(null);
    try {
      const reason = window.prompt('修正原因');
      if (!reason) return;
      const pw = window.prompt(`二次认证：输入账户口令以修正 #${e.seq}`);
      if (!pw) return;
      const sig = await createSig.mutateAsync({ action: 'lab.correct', resourceType: 'lab', resourceId: e.id, meaning: `修正化验 #${e.seq}`, payload: { reason }, password: pw });
      await correct.mutateAsync({ id: e.id, body: { sampleAt: e.sampleAt, ph: e.ph, nh3n: e.nh3n, no2: e.no2, reason, signatureId: sig.id } });
      setMsg('修正已提交（追加新版本，已电子签名）');
    } catch (err) {
      setMsg((err as Error).message);
    }
  };

  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-[24px] font-bold text-slate-900">化验双向闭环（PR4）</h2>
        </div>
        <p className="mt-1 text-[14px] text-slate-500">持久化录入、PLC 快照交叉校验与校准提议；演示环境接口公开，生产须鉴权或经 BFF。</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: entry form */}
        <Card className="p-6">
          <div className="mb-4 text-[16px] font-semibold text-slate-800">水质化验录入</div>
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-500">
            <span>操作员：{user?.displayName ?? '未登录'}</span>
            {user ? <StatusPill health="normal" /> : <Badge className="bg-alarm-bg text-alarm-fg border-alarm-border">未登录</Badge>}
          </div>
          <form onSubmit={doSubmit}>
            <label className="mb-1 block text-[13px] text-slate-500">采样时间 * <span className="text-slate-400">工厂时区（IANA）：Asia/Shanghai</span></label>
            <input type="datetime-local" value={sampleAt} onChange={(e) => setSampleAt(e.target.value)} className="mb-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
            <div className="mb-4 text-[12px] text-slate-400">选择实际取样的时间（精确到分钟，允许回溯过去）</div>

            {([
              ['pH', ph, setPh, '0–14', '0 - 14'],
              ['氨氮 (mg/L)', nh3n, setNh3n, '0–100', '0 - 100 mg/L'],
              ['亚盐 (mg/L)', no2, setNo2, '0–50', '0 - 50 mg/L'],
            ] as const).map(([label, val, set, , range]) => (
              <div key={label} className="mb-3">
                <label className="mb-1 block text-[13px] text-slate-500">{label}</label>
                <input value={val} onChange={(e) => set(e.target.value)} placeholder={range} className="w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
                <div className="mt-1 text-[12px] text-slate-400">有效范围：{range}</div>
              </div>
            ))}

            <div className="mt-2 rounded-lg bg-warn-bg/60 px-3 py-2 text-[12px] text-warn-fg">
              关注阈值 NH₃-N&gt;{LAB_THRESHOLDS.nh3nWarn} 或 NO₂-N&gt;{LAB_THRESHOLDS.no2Warn}；极危（联动救灾模拟）为 &gt;{LAB_THRESHOLDS.nh3nCritical} / &gt;{LAB_THRESHOLDS.no2Critical}。
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button disabled={!user || submit.isPending} className="btn btn-dark disabled:opacity-50">提交化验数据</button>
              {msg ? <span className="text-[13px] text-slate-500">{msg}</span> : null}
            </div>
          </form>
        </Card>

        {/* Right: records + chain + calibration */}
        <div className="space-y-6">
          <Card className="p-6">
            <SectionTitle title="化验记录" desc="点击一行查看审计链；可对生效记录发起修正。" />
            <DataTable
              columns={[
                { key: 'seq', header: '版本', render: (e: LabEntry) => <span className="tabular-nums">#{e.seq}</span> },
                { key: 'sampleId', header: '样品号', render: (e: LabEntry) => <span className="font-mono text-[12px] text-slate-500">{e.sampleId ?? '—'}</span> },
                { key: 'sampleAt', header: '采样时间', render: (e: LabEntry) => <span className="tabular-nums text-slate-500">{ts(e.sampleAt)}</span> },
                { key: 'nh3n', header: '氨氮', render: (e: LabEntry) => <span className="tabular-nums">{e.nh3n ?? '—'}</span> },
                { key: 'no2', header: '亚盐', render: (e: LabEntry) => <span className="tabular-nums">{e.no2 ?? '—'}</span> },
                { key: 'bypass', header: '校验', render: (e: LabEntry) => (e.bypass ? <Badge className="bg-warn-bg text-warn-fg border-warn-border">旁路</Badge> : <Badge className="bg-ok-bg text-ok-fg border-ok-border">正常</Badge>) },
                {
                  key: 'ops',
                  header: '操作',
                  render: (e: LabEntry) => (
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelectedId(e.id)} className={selectedId === e.id ? 'text-brand-700 font-semibold' : 'text-brand-600 hover:underline'}>
                        {selectedId === e.id ? '查看中' : '查看链'}
                      </button>
                      <button onClick={() => doCorrect(e)} disabled={correct.isPending} className="text-slate-500 hover:underline">修正</button>
                    </div>
                  ),
                },
              ]}
              rows={effective}
              empty={<EmptyHint>暂无历史版本</EmptyHint>}
            />
          </Card>

          <Card className="p-6">
            <SectionTitle title="审计追溯链（不可变账本）" right={<Badge>{lab?.totalVersions ?? 0} 个版本</Badge>} />
            {chain && chain.length > 0 ? (
              <ol className="space-y-2">
                {chain.map((e) => (
                  <li key={e.id} className="rounded-lg border border-hairline px-3 py-2 text-[13px]">
                    <div className="flex justify-between"><b>#{e.seq} · {ts(e.sampleAt)}</b><span className="text-slate-400">{e.operatorName}</span></div>
                    {e.correctionReason ? <div className="mt-1 text-[12px] text-warn-fg">修正：{e.correctionReason}</div> : null}
                  </li>
                ))}
              </ol>
            ) : (
              <EmptyHint>
                <IconClock />
                暂无历史版本
              </EmptyHint>
            )}
            <div className="mt-3 text-[12px] text-slate-400">
              Append-Only 不可变账本：仅追加新版本并用 previousEntryId 指向旧版本；历史行永不 UPDATE。当前生效记录在查询侧由链推导（叶子节点）。
            </div>
          </Card>

          <Card className="p-6">
            <SectionTitle title="PLC 校准提议" desc="须绑定化验记录；PLC 交叉校验旁路的化验禁止发起校准；同通道演示互斥。" />
            <div className="grid grid-cols-3 gap-3">
              <label className="text-[13px] text-slate-500">指标
                <select value={calMetric} onChange={(e) => setCalMetric(e.target.value as 'ph' | 'nh3n' | 'no2')} className="mt-1 w-full rounded-lg border border-hairline px-2 py-2 text-sm">
                  <option value="nh3n">氨氮</option>
                  <option value="no2">亚盐</option>
                  <option value="ph">pH</option>
                </select>
              </label>
              <label className="text-[13px] text-slate-500">通道标识
                <input value={calChannel} onChange={(e) => setCalChannel(e.target.value)} className="mt-1 w-full rounded-lg border border-hairline px-2 py-2 text-sm" />
              </label>
              <label className="text-[13px] text-slate-500">绝对偏移
                <input value={calOffset} onChange={(e) => setCalOffset(e.target.value)} className="mt-1 w-full rounded-lg border border-hairline px-2 py-2 text-sm" />
              </label>
            </div>
            <button
              onClick={doCalibrate}
              disabled={!selected || selected.bypass || calibrate.isPending}
              className="btn btn-dark mt-4 disabled:opacity-50"
            >
              发起校准提议
            </button>
            {!selected ? <span className="ml-3 text-[13px] text-slate-400">请先在「化验记录」选择一条生效记录</span> : null}
          </Card>
        </div>
      </div>

      <QcPanel />
    </div>
  );
}

/** D1: Shewhart control-chart QC over a metric's effective series. */
function QcPanel() {
  const [metric, setMetric] = useState<'ph' | 'nh3n' | 'no2'>('nh3n');
  const { data } = useLabQc(metric);
  const qc = data;
  const bad = (qc?.outOfControl.length ?? 0) > 0;
  const warn = !bad && (qc?.warning.length ?? 0) > 0;
  return (
    <Card className="mt-6 p-6">
      <SectionTitle
        title="方法质控 · 控制图（D1）"
        desc="对指标的有效化验序列计算均值/标准差与 ±2σ 警告限、±3σ 失控限（Shewhart）；失控点提示复查试剂/标液/仪器。"
        right={
          <select value={metric} onChange={(e) => setMetric(e.target.value as 'ph' | 'nh3n' | 'no2')} className="rounded-lg border border-hairline px-2 py-1 text-sm">
            <option value="nh3n">氨氮</option><option value="no2">亚盐</option><option value="ph">pH</option>
          </select>
        }
      />
      {!qc || qc.n < 3 ? (
        <div className="text-[13px] text-slate-400">有效样本不足（需 ≥3 点）以建立控制图；随化验累积自动生成。</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div><div className="text-[12px] text-slate-400">均值 μ</div><div className="text-[20px] font-semibold tabular-nums text-slate-800">{qc.mean}</div></div>
          <div><div className="text-[12px] text-slate-400">标准差 σ</div><div className="text-[20px] font-semibold tabular-nums text-slate-800">{qc.sd}</div></div>
          <div><div className="text-[12px] text-slate-400">控制限 ±3σ</div><div className="text-[20px] font-semibold tabular-nums text-slate-800">{qc.lcl} ~ {qc.ucl}</div></div>
          <div>
            <div className="text-[12px] text-slate-400">判定（n={qc.n}）</div>
            {bad ? <Badge className="bg-alarm-bg text-alarm-fg border-alarm-border">失控 {qc.outOfControl.length} 点</Badge>
              : warn ? <Badge className="bg-warn-bg text-warn-fg border-warn-border">警戒 {qc.warning.length} 点</Badge>
                : <Badge className="bg-ok-bg text-ok-fg border-ok-border">受控</Badge>}
          </div>
        </div>
      )}
    </Card>
  );
}

function IconClock() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
